// Graph API Service - User management, profile, groups, email
import { graphConfig, groupConfig } from '../msalConfig';

const GRAPH = graphConfig.graphEndpoint;
const TENANT_DOMAIN = 'globaliqhub.onmicrosoft.com';

/**
 * Helper to make authenticated Graph API calls
 */
async function graphFetch(url, token, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!res.ok) {
    const errorBody = await res.text();
    let errorMsg;
    try {
      const parsed = JSON.parse(errorBody);
      errorMsg = parsed.error?.message || `Graph API error: ${res.status}`;
    } catch {
      errorMsg = `Graph API error: ${res.status} - ${errorBody}`;
    }
    throw new Error(errorMsg);
  }

  if (res.status === 204) return null;
  return res.json();
}

// ========================
// User Profile
// ========================

export async function getMyProfile(token) {
  return graphFetch(`${GRAPH}/me`, token);
}

export async function getMyGroups(token) {
  const data = await graphFetch(
    `${GRAPH}/me/memberOf?$select=id,displayName,description`,
    token
  );
  return (data.value || []).filter(
    (item) => item['@odata.type'] === '#microsoft.graph.group'
  );
}

// ========================
// Password Management
// ========================

export async function changeMyPassword(token, currentPassword, newPassword) {
  return graphFetch(`${GRAPH}/me/changePassword`, token, {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function resetUserPassword(token, userId, newPassword) {
  return graphFetch(`${GRAPH}/users/${userId}`, token, {
    method: 'PATCH',
    body: JSON.stringify({
      passwordProfile: {
        password: newPassword,
        forceChangePasswordNextSignIn: false,
      },
    }),
  });
}

// ========================
// User Management (Admin)
// ========================

export async function listGroupMembers(token, groupId) {
  const data = await graphFetch(
    `${GRAPH}/groups/${groupId}/members?$select=id,displayName,mail,userPrincipalName,accountEnabled,createdDateTime`,
    token
  );
  return data.value || [];
}

export async function listAllManagedUsers(token) {
  const adminId = groupConfig.goaAdminGroupId;
  const memberId = groupConfig.goaMembersGroupId;

  const results = [];
  const seen = new Set();

  for (const { groupId, role } of [
    { groupId: adminId, role: 'admin' },
    { groupId: memberId, role: 'member' },
  ]) {
    if (!groupId) continue;
    try {
      const members = await listGroupMembers(token, groupId);
      for (const m of members) {
        if (!seen.has(m.id)) {
          seen.add(m.id);
          results.push({
            id: m.id,
            displayName: m.displayName,
            email: m.mail || m.userPrincipalName,
            accountEnabled: m.accountEnabled,
            role,
            createdDateTime: m.createdDateTime,
          });
        }
      }
    } catch (err) {
      console.error(`Failed to list members of group ${groupId}:`, err);
    }
  }

  return results;
}

export async function createUser(token, { email, displayName, password }) {
  const mailNickname = email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');

  return graphFetch(`${GRAPH}/users`, token, {
    method: 'POST',
    body: JSON.stringify({
      accountEnabled: true,
      displayName,
      mailNickname,
      passwordProfile: {
        password,
        forceChangePasswordNextSignIn: false,
      },
      identities: [
        {
          signInType: 'emailAddress',
          issuer: TENANT_DOMAIN,
          issuerAssignedId: email,
        },
      ],
    }),
  });
}

export async function deleteUser(token, userId) {
  return graphFetch(`${GRAPH}/users/${userId}`, token, {
    method: 'DELETE',
  });
}

export async function addUserToGroup(token, groupId, userId) {
  return graphFetch(`${GRAPH}/groups/${groupId}/members/$ref`, token, {
    method: 'POST',
    body: JSON.stringify({
      '@odata.id': `${GRAPH}/directoryObjects/${userId}`,
    }),
  });
}

export async function removeUserFromGroup(token, groupId, userId) {
  return graphFetch(`${GRAPH}/groups/${groupId}/members/${userId}/$ref`, token, {
    method: 'DELETE',
  });
}

// ========================
// Email
// ========================

export async function sendWelcomeEmail(token, { toEmail, toName, tempPassword }) {
  const appUrl = window.location.origin;

  const htmlBody = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 2rem; border-radius: 12px 12px 0 0; text-align: center;">
        <h1 style="color: white; margin: 0; font-size: 1.5rem;">🌴 Goa Resort Investment</h1>
        <p style="color: rgba(255,255,255,0.9); margin: 0.5rem 0 0; font-size: 0.95rem;">Secure Briefing Platform</p>
      </div>
      <div style="background: white; padding: 2rem; border: 1px solid #e0e0e0; border-top: none;">
        <h2 style="color: #333; margin-top: 0;">Welcome, ${toName}!</h2>
        <p style="color: #555; line-height: 1.6;">
          You have been granted access to the Goa Resort Investment Briefing Platform.
          Below are your login credentials:
        </p>
        <div style="background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; padding: 1.25rem; margin: 1.5rem 0;">
          <p style="margin: 0 0 0.5rem; color: #666; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em;">Login Credentials</p>
          <p style="margin: 0.25rem 0; color: #333;"><strong>Email:</strong> ${toEmail}</p>
          <p style="margin: 0.25rem 0; color: #333;"><strong>Temporary Password:</strong> <code style="background: #e9ecef; padding: 0.2rem 0.5rem; border-radius: 4px; font-size: 1rem;">${tempPassword}</code></p>
        </div>
        <p style="color: #555; line-height: 1.6;">
          Please sign in at <a href="${appUrl}" style="color: #667eea;">${appUrl}</a>
          and change your password from your Profile page after your first login.
        </p>
        <div style="background: #fff3cd; border-left: 4px solid #ffc107; padding: 1rem; border-radius: 4px; margin-top: 1.5rem;">
          <p style="margin: 0; color: #664d03; font-size: 0.9rem;">
            ⚠️ <strong>Security Notice:</strong> This platform contains confidential investment materials.
            Do not share your credentials with anyone.
          </p>
        </div>
      </div>
      <div style="background: #f8f9fa; padding: 1rem 2rem; border-radius: 0 0 12px 12px; border: 1px solid #e0e0e0; border-top: none; text-align: center;">
        <p style="margin: 0; color: #999; font-size: 0.8rem;">
          This is an automated message from the Goa Resort Investment Platform.
        </p>
      </div>
    </div>`;

  return graphFetch(`${GRAPH}/me/sendMail`, token, {
    method: 'POST',
    body: JSON.stringify({
      message: {
        subject: '🌴 Welcome to Goa Resort Investment Briefing Platform',
        body: {
          contentType: 'HTML',
          content: htmlBody,
        },
        toRecipients: [
          {
            emailAddress: {
              address: toEmail,
              name: toName,
            },
          },
        ],
      },
      saveToSentItems: true,
    }),
  });
}

// ========================
// Utility
// ========================

export function generateTempPassword() {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const special = '!@#$%&*';
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let pw = [pick(upper), pick(lower), pick(digits), pick(special)];
  const all = upper + lower + digits + special;
  for (let i = 0; i < 8; i++) pw.push(pick(all));
  for (let i = pw.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pw[i], pw[j]] = [pw[j], pw[i]];
  }
  return pw.join('');
}
